import {take, put, select, fork} from 'redux-saga/effects';
import moment from 'moment';
import {invoiceSubmissionParams} from './constants/queryParams';
import * as userSelectors from '../user-management/userManagementSelectors';
import * as invoiceSubmissionActionTypes from './invoiceSubmissionActionTypes';
import fetchRequest from '../application/sagas/fetchRequest';
import * as errorActionTypes from '../application/errorActionTypes';
import {parseErrorMessages} from '../utils/validationMessages';
import * as invoiceSubmissionSelectors from './invoiceSubmissionSelectors';
import {formAnchors} from './constants/formAnchors';
import {apsPricingTypes} from './constants/apsPricingTypes';
import {invoiceSubmissionViews} from './constants/invoiceSubmissionViews';
import {eafViewFromStatus} from './constants/invoiceSubmissionStatuses';
import getPricingInfoFlow from '../pricing/sagas/getPricingInfoFlow';
import {InvoiceSubmissionDataStates} from './constants/invoiceSubmissionDataStates';
import getMainServicesForSubCaseFlow from './sagas/getMainServicesForSubCaseFlow';
import {eafStatuses} from '../ella-dispo-entity-types';
import invoiceSubmissionFiles from '../invoiceSubmissionFiles';
import getInvoiceSubmissionMessages from '../messages/sagas/getInvoiceSubmissionMessages';

export const loadOpenInvoiceSubmissions = function* loadOpenInvoiceSubmissions() {
    const query = invoiceSubmissionParams.OPEN;
    const userAccount = yield select(userSelectors.getUserAccount);
    const isSearchViewActive = yield select(invoiceSubmissionSelectors.getIsSearchViewActive);
    if (isSearchViewActive) {
        yield put({type: invoiceSubmissionActionTypes.CLEAR_INVOICE_SUBMISSION_SEARCH_RESULTS});
    }
    const {serviceManager} = yield select(state => state.application);
    const eafService = serviceManager.loadService('ellaAssignmentFlowService');

    yield put({
        type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
        payload: {state: InvoiceSubmissionDataStates.LOADING},
    });
    yield put({type: invoiceSubmissionActionTypes.CLEAR_INVOICE_SUBMISSION_DATA});
    yield fork(
        fetchRequest,
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSIONS_REQUEST,
        eafService.getInvoiceSubmissions,
        {...query, contractPartnerIds: userAccount.partnerIds},
    );
    const resultAction = yield take([
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSIONS_REQUEST_SUCCEEDED,
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSIONS_REQUEST_FAILED,
    ]);
    if (!resultAction.error) {
        const {data, totalCount} = resultAction.payload.response;
        yield put({
            type: invoiceSubmissionActionTypes.STORE_OPEN_INVOICE_SUBMISSIONS,
            payload: {invoiceSubmissionsDTO: data, totalCount},
        });
        yield put({
            type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
            payload: {state: InvoiceSubmissionDataStates.IDLE},
        });
        return;
    }
    yield put({
        type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
        payload: {state: InvoiceSubmissionDataStates.IDLE},
    });
    yield put({
        type: errorActionTypes.STORE_ERRORS,
        payload: {
            error: {
                ...parseErrorMessages(resultAction.payload),
            },
        },
    });
};

export const loadSubmittedInvoiceSubmissions = function* loadSubmittedInvoiceSubmissions() {
    const query = invoiceSubmissionParams.SUBMITTED;
    const userAccount = yield select(userSelectors.getUserAccount);
    const isSearchViewActive = yield select(invoiceSubmissionSelectors.getIsSearchViewActive);
    if (isSearchViewActive) {
        yield put({type: invoiceSubmissionActionTypes.CLEAR_INVOICE_SUBMISSION_SEARCH_RESULTS});
    }
    const {serviceManager} = yield select(state => state.application);
    const eafService = serviceManager.loadService('ellaAssignmentFlowService');

    yield put({
        type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
        payload: {state: InvoiceSubmissionDataStates.LOADING},
    });
    yield put({type: invoiceSubmissionActionTypes.CLEAR_INVOICE_SUBMISSION_DATA});
    yield fork(
        fetchRequest,
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSIONS_REQUEST,
        eafService.getInvoiceSubmissions,
        {...query, contractPartnerIds: userAccount.partnerIds},
    );
    const resultAction = yield take([
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSIONS_REQUEST_SUCCEEDED,
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSIONS_REQUEST_FAILED,
    ]);
    if (!resultAction.error) {
        const {data} = resultAction.payload.response;
        yield put({
            type: invoiceSubmissionActionTypes.STORE_SUBMITTED_INVOICE_SUBMISSIONS,
            payload: {invoiceSubmissionsDTO: data},
        });
        yield put({
            type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
            payload: {state: InvoiceSubmissionDataStates.IDLE},
        });
        return;
    }
    yield put({
        type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
        payload: {state: InvoiceSubmissionDataStates.IDLE},
    });
    yield put({
        type: errorActionTypes.STORE_ERRORS,
        payload: {
            error: {
                ...parseErrorMessages(resultAction.payload),
            },
        },
    });
};

export const loadArchiveInvoiceSubmissions = function* loadArchiveInvoiceSubmissions(params) {
    const isSearchViewActive = yield select(invoiceSubmissionSelectors.getIsSearchViewActive);
    if (isSearchViewActive) {
        yield put({type: invoiceSubmissionActionTypes.CLEAR_INVOICE_SUBMISSION_SEARCH_RESULTS});
    }
    const {search} = params.payload.location;
    const {size} = params;
    const query = invoiceSubmissionParams.ARCHIVE;
    const userAccount = yield select(userSelectors.getUserAccount);
    const {serviceManager} = yield select(state => state.application);
    const eafService = serviceManager.loadService('ellaAssignmentFlowService');
    const queryObject = Object.fromEntries(new URLSearchParams(search));
    const {page, statuses, mainServices, fromServiceStartDateTime} = queryObject;
    const {toServiceEndDateTime, sortBy, direction} = queryObject;
    const date = {
        ...(fromServiceStartDateTime ? {fromServiceStartDateTime} : {}),
        ...(toServiceEndDateTime ? {toServiceEndDateTime} : {}),
    };


    yield put({
        type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
        payload: {state: InvoiceSubmissionDataStates.LOADING},
    });
    yield put({type: invoiceSubmissionActionTypes.CLEAR_INVOICE_SUBMISSION_DATA});

    yield fork(
        fetchRequest,
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSIONS_REQUEST,
        eafService.getInvoiceSubmissions,
        {
            ...query,
            contractPartnerIds: userAccount.partnerIds,
            ...(size ? {size} : {}),
            ...(statuses ? {statuses} : {}),
            ...(page && {page}),
            ...(mainServices && {mainServices}),
            ...(sortBy ? {sortBy} : {sortBy: 'SERVICE_START_DATE'}),
            ...(direction ? {direction} : {direction: 'DESC'}),
            ...(date || {}),
        },
    );

    const resultAction = yield take([
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSIONS_REQUEST_SUCCEEDED,
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSIONS_REQUEST_FAILED,
    ]);

    if (!resultAction.error) {
        const {data, totalCount, totalInvoicedCount} = resultAction.payload.response;

        yield put({
            type: invoiceSubmissionActionTypes.STORE_ARCHIVE_INVOICE_SUBMISSIONS,
            payload: {
                invoiceSubmissionsDTO: data,
                totalCount: totalCount,
                totalInvoicesCount: totalInvoicedCount,
            },
        });

        yield put({
            type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
            payload: {state: InvoiceSubmissionDataStates.IDLE},
        });
    } else {
        yield put({
            type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
            payload: {state: InvoiceSubmissionDataStates.IDLE},
        });
        yield put({
            type: errorActionTypes.STORE_ERRORS,
            payload: {
                error: {
                    ...parseErrorMessages(resultAction.payload),
                },
            },
        });
    }
};

// todo fix and revisit this
export const loadInvoiceSubmission = function* loadInvoiceSubmission({payload}) {
    const {match, location} = payload;
    const {invoiceSubmissionId} = match.params;
    const {invoiceSubmissionStatus} = location;
    const {serviceManager} = yield select(state => state.application);
    const eafService = serviceManager.loadService('ellaAssignmentFlowService');

    const shouldSetInEditing = invoiceSubmissionStatus === eafStatuses.BILLABLE;

    yield put({
        type: invoiceSubmissionActionTypes.STORE_INVOICE_SUBMISSION_VIEW_STATE,
        payload: {invoiceSubmissionViewState: null, formAnchor: null},
    });

    yield put({
        type: invoiceSubmissionFiles.actionTypes.STORE_FILES,
        payload: {files: []},
    });

    yield put({type: errorActionTypes.DELETE_ERRORS});

    yield put({
        type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
        payload: {state: InvoiceSubmissionDataStates.LOADING},
    });
    yield put({
        type: invoiceSubmissionActionTypes.STORE_FOLLOW_UP_INVOICE_SUBMISSION_MAIN_SERVICES,
        payload: {mainServices: null},
    });
    yield put({type: invoiceSubmissionActionTypes.CLEAR_INVOICE_SUBMISSION_DATA});
    yield fork(
        fetchRequest,
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSION_REQUEST,
        eafService.getInvoiceSubmission,
        {invoiceSubmissionId, shouldSetInEditing},
    );

    const resultAction = yield take([
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSION_REQUEST_SUCCEEDED,
        invoiceSubmissionActionTypes.GET_INVOICE_SUBMISSION_REQUEST_FAILED,
    ]);

    if (!resultAction.error) {
        const {response: invoiceSubmission} = resultAction.payload;
        const apmService = serviceManager.loadService('partnerManagementService');

        yield put({
            type: invoiceSubmissionActionTypes.SET_PRICING_DATA_READY,
            payload: {isPricingDataReady: false},
        });
        yield fork(getPricingInfoFlow, {
            payload: {
                contractPartnerId: invoiceSubmission.assignment?.contractPartnerId,
                serviceEndDateTime: invoiceSubmission.assignment?.serviceEndDateTime || moment().format(),
                type: 'CONTRACT_PARTNER_FIXED_PRICE',
            },
        });

        yield fork(getInvoiceSubmissionMessages, {invoiceSubmissionId: invoiceSubmission.invoiceSubmissionId});

        if (invoiceSubmission.invoiceSubmissionNumber !== 'A1') {
            yield fork(getMainServicesForSubCaseFlow, {
                payload: {
                    invoiceSubmissionId: invoiceSubmission.assignment.assignmentId + 'A1',
                },
            });
        }

        yield fork(
            fetchRequest,
            invoiceSubmissionActionTypes.GET_CONTRACT_PARTNER_INFO_REQUEST,
            apmService.getACEPartner,
            {acePartnerId: invoiceSubmission.assignment.contractPartnerId},
        );

        const resultContractPartnerAction = yield take([
            invoiceSubmissionActionTypes.GET_CONTRACT_PARTNER_INFO_REQUEST_SUCCEEDED,
            invoiceSubmissionActionTypes.GET_CONTRACT_PARTNER_INFO_REQUEST_FAILED,
        ]);

        if (!resultContractPartnerAction.error) {
            const {location} = payload;
            const {isFormOpen} = location;
            const shouldOpenForm = location.query.isFormOpen;
            const invoiceSubmissionViewState = isFormOpen || shouldOpenForm
                ? invoiceSubmissionViews.FORM : eafViewFromStatus[invoiceSubmission.status];
            const formAnchor = isFormOpen || shouldOpenForm ? formAnchors.ADDITIONAL_SERVICE_FORM : '';
            const {acePartnerDTO: contractPartnerDTO} = resultContractPartnerAction.payload.response;
            yield put({
                type: invoiceSubmissionActionTypes.STORE_INVOICE_SUBMISSION_VIEW_STATE,
                payload: {invoiceSubmissionViewState, formAnchor},
            });
            yield put({
                type: invoiceSubmissionActionTypes.STORE_INVOICE_SUBMISSION,
                payload: {
                    invoiceSubmissionDTO: {
                        ...invoiceSubmission,
                        isFirstOpening: shouldSetInEditing,
                        assignment: {
                            ...invoiceSubmission.assignment,
                            contractPartnerInfo: contractPartnerDTO,
                        },
                    },
                },
            });

            yield put({
                type: invoiceSubmissionFiles.actionTypes.STORE_FILES,
                payload: {files: invoiceSubmission.attachments},
            });

            yield put({
                type: invoiceSubmissionActionTypes.SET_INVOICE_SUBMISSION_REQUEST_STATE,
                payload: {state: InvoiceSubmissionDataStates.IDLE},
            });
            return;
        }
        yield put({
            type: errorActionTypes.STORE_ERRORS,
            payload: {
                error: {
                    ...parseErrorMessages(resultAction.payload),
                },
            },
        });
        return;
    }
    yield put({
        type: errorActionTypes.STORE_ERRORS,
        payload: {
            error: {
                ...parseErrorMessages(resultAction.payload),
            },
        },
    });
};

export const loadValidServiceFixedPrices = function* loadValidServiceFixedPrices({payload}) {
    const {serviceManager} = yield select(state => state.application);
    const pricingManagementService = serviceManager.loadService('pricingManagementService');
    const invoiceSubmissions = yield select(state => state.invoiceSubmission.invoiceSubmissions);
    const {match} = payload;
    const {invoiceSubmissionId} = match?.params;
    const invoiceSubmission = invoiceSubmissions[invoiceSubmissionId];
    const {assignment} = invoiceSubmission;

    const searchQueryParams = new URLSearchParams();
    searchQueryParams.append('validOn', moment(assignment.endDate).format('YYYY-MM-DD'));
    searchQueryParams.set('type', apsPricingTypes.SERVICE_FIXED_PRICE);

    yield fork(
        fetchRequest,
        invoiceSubmissionActionTypes.GET_SERVICE_FIXED_PRICE_REQUEST,
        pricingManagementService.getValidPrice,
        {searchQueryParams},
    );

    const serviceFixedPriceResponse = yield take([
        invoiceSubmissionActionTypes.GET_SERVICE_FIXED_PRICE_REQUEST_SUCCEEDED,
        invoiceSubmissionActionTypes.GET_SERVICE_FIXED_PRICE_REQUEST_FAILED,
    ]);

    if (!serviceFixedPriceResponse.error) {
        const {response} = serviceFixedPriceResponse.payload;
        const {pricePeriodDTOs} = response;

        yield put({
            type: invoiceSubmissionActionTypes.STORE_SERVICE_FIXED_PRICES,
            payload: {pricePeriodDTOs},
        });
    }
    yield put({
        type: invoiceSubmissionActionTypes.SET_PRICING_DATA_READY,
        payload: {isPricingDataReady: true},
    });
};

export const loadOpenCarMaxPrice = function* loadOpenCarMaxPrice({payload}) {
    const {serviceManager} = yield select(state => state.application);
    const pricingManagementService = serviceManager.loadService('pricingManagementService');
    const invoiceSubmissions = yield select(state => state.invoiceSubmission.invoiceSubmissions);
    const {match} = payload;
    const {invoiceSubmissionId} = match?.params;
    const invoiceSubmission = invoiceSubmissions[invoiceSubmissionId];
    const {assignment} = invoiceSubmission;
    const {member} = assignment;

    yield put({
        type: invoiceSubmissionActionTypes.STORE_OPEN_CAR_MAX_PRICES,
        payload: {openCarMaxPrice: null},
    });

    const searchQueryParams = new URLSearchParams();
    searchQueryParams.append('validOn', moment(assignment.endDate).format('YYYY-MM-DD'));
    searchQueryParams.set('type', apsPricingTypes.OPENING_MAX_PRICE);
    searchQueryParams.set('memberTariff', member ? member.tariffDetails?.tariffGroup || 'CLASSIC' : 'CLASSIC');

    yield fork(
        fetchRequest,
        invoiceSubmissionActionTypes.GET_OPEN_CAR_MAX_PRICE_REQUEST,
        pricingManagementService.getValidPrice,
        {searchQueryParams},
    );

    const serviceFixedPriceResponse = yield take([
        invoiceSubmissionActionTypes.GET_OPEN_CAR_MAX_PRICE_REQUEST_SUCCEEDED,
        invoiceSubmissionActionTypes.GET_OPEN_CAR_MAX_PRICE_REQUEST_FAILED,
    ]);

    if (!serviceFixedPriceResponse.error) {
        const {response} = serviceFixedPriceResponse.payload;
        const {pricePeriodDTOs} = response;
        const openCarMaxPeriod = pricePeriodDTOs[0];

        yield put({
            type: invoiceSubmissionActionTypes.STORE_OPEN_CAR_MAX_PRICES,
            payload: {openCarMaxPrice: openCarMaxPeriod?.value || null},
        });
    }
};
